import { useState, useCallback, useEffect, useMemo } from 'react'
import { Eye, Plus, Settings2, TreePalm, X } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { Badge, Button, SegmentedControl } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { EmptyState } from '@/components/common/EmptyState'
import {
  TableRowAction,
  TableRowActions,
} from '@/components/common/TableRowActions'
import {
  DataTable,
  DataTableCard,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter'
import { useLeavesWorkspace } from '@/features/leave/hooks/useLeaves'
import { useMe } from '@/features/settings/hooks/useMe'
import { usePermissions } from '@/hooks/usePermissions'
import { formatDateTime, formatRelative, userSubline } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import type {
  LeaveDocument,
  LeaveStatus,
  MyLeaveListQuery,
} from '@/features/leave/types/leave.types'
import {
  canDecideLeave,
  formatLeaveLength,
  formatLeaveRange,
  getRoleSlug,
  leaveStatusBadgeVariant,
  leaveStatusLabel,
  leaveTiming,
  leaveTypeLabel,
  type LeaveTiming,
} from './leaveUtils'
import { LeaveDetailModal } from './LeaveDetailModal'
import { CreateLeaveModal } from './CreateLeaveModal'
import { LeaveTypesModal } from './LeaveTypesModal'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const
type StatusFilter = 'all' | LeaveStatus

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_admin', label: 'Admin review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

const TIMING_TONE: Record<LeaveTiming['tone'], string> = {
  now: 'text-emerald-700 dark:text-emerald-400',
  soon: 'text-amber-700 dark:text-amber-400',
  later: 'text-gray-500 dark:text-gray-400',
  past: 'text-gray-400 dark:text-gray-500',
}

export function WorkspaceLeavesPage(): React.ReactElement {
  const { can } = usePermissions()
  const { data: me } = useMe()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const filter = useWorkspaceCompanyFilter({ onChange: () => setPage(1) })
  const [selected, setSelected] = useState<LeaveDocument | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const canCreateLeave = can('leave', 'create_self')
  const canApprove = can('leave', 'approve') || can('leave', 'reject')
  const canManageTypes = can('leave', 'manage_types')
  const [typesOpen, setTypesOpen] = useState(false)
  const roleSlug = getRoleSlug(me)
  const selectedCompanyIds = Array.from(filter.selectedCompanyIds)
  const createCompanyId =
    selectedCompanyIds.length === 1
      ? selectedCompanyIds[0]
      : filter.companies.length === 1
        ? filter.companies[0]._id
        : null

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [search])

  const query = useMemo<MyLeaveListQuery>(
    () => ({
      page,
      limit,
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: debouncedSearch || undefined,
      companyIds: filter.companyIds,
    }),
    [page, limit, statusFilter, debouncedSearch, filter.companyIds]
  )

  const { data, isLoading, error, refetch } = useLeavesWorkspace(query)

  const leaves = useMemo(() => data?.data ?? [], [data?.data])
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const hasActiveFilters =
    filter.selectedCompanyIds.size > 0 ||
    statusFilter !== 'all' ||
    Boolean(debouncedSearch)

  const companyById = useMemo(() => {
    const map = new Map<string, { name: string; color?: string }>()
    for (const company of filter.companies) {
      map.set(company._id, { name: company.name, color: company.color })
    }
    return map
  }, [filter.companies])

  const openLeave = useCallback((leave: LeaveDocument) => {
    setSelected(leave)
  }, [])

  /** Pointer convenience; the row's action carries the same behaviour for keyboards. */
  const handleRowActivate = (leave: LeaveDocument): void => {
    if (window.getSelection()?.toString()) return
    openLeave(leave)
  }

  const handleResetFilters = (): void => {
    filter.reset()
    setStatusFilter('all')
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
  }

  /**
   * Everything a row shows, derived once so the table and the mobile card list
   * can never drift apart.
   */
  const describeLeave = (leave: LeaveDocument) => {
    const displayName =
      leave.requesterName?.trim() || leave.requesterEmail || 'Unknown employee'
    const email = leave.requesterEmail?.trim()
    return {
      displayName,
      // Secondary line only carries what the name doesn't already say.
      subline: userSubline(
        leave.requesterEmployeeId,
        email && email !== displayName ? email : undefined
      ),
      company: companyById.get(leave.companyId),
      companyColor: getCompanyColor(leave.companyId, companyById.get(leave.companyId)?.color),
      type: leaveTypeLabel(leave.leaveType),
      range: formatLeaveRange(leave.startDate, leave.endDate),
      length: formatLeaveLength(leave),
      timing: leaveTiming(leave),
      submitted: leave.createdAt ? formatRelative(leave.createdAt) : '—',
      submittedTitle: leave.createdAt ? formatDateTime(leave.createdAt) : undefined,
      needsReview: canDecideLeave(leave.status, canApprove, roleSlug),
    }
  }

  type Row = ReturnType<typeof describeLeave>

  const renderPerson = (row: Row, size: 'md' | 'lg'): React.ReactElement => (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={row.displayName} size={size} tone="soft" />
      <div className="min-w-0">
        <p className="truncate font-medium text-gray-900 dark:text-gray-100">
          {row.displayName}
        </p>
        {row.subline ? (
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {row.subline}
          </p>
        ) : null}
      </div>
    </div>
  )

  const renderCompany = (row: Row): React.ReactElement => (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: row.companyColor.dot }}
      />
      <span className="truncate text-gray-700 dark:text-gray-300">
        {row.company?.name ?? '—'}
      </span>
    </span>
  )

  /** "5 – 7 Oct 2026" over "3 days · Starts tomorrow". */
  const renderDates = (row: Row): React.ReactElement => (
    <div className="min-w-0">
      <p className="truncate tabular-nums text-gray-900 dark:text-gray-100">
        {row.range}
      </p>
      <p className="truncate text-xs tabular-nums text-gray-500 dark:text-gray-400">
        {row.length}
        {row.timing ? (
          <>
            <span aria-hidden> · </span>
            <span className={cn('font-medium', TIMING_TONE[row.timing.tone])}>
              {row.timing.label}
            </span>
          </>
        ) : null}
      </p>
    </div>
  )

  const renderStatus = (leave: LeaveDocument, size?: 'sm'): React.ReactElement => (
    <Badge variant={leaveStatusBadgeVariant(leave.status)} dot size={size}>
      {leaveStatusLabel(leave.status)}
    </Badge>
  )

  /**
   * Requests the viewer can decide get a visible "Review" button — it's the
   * work this page exists for. Everything else is a quiet hover action.
   */
  const renderAction = (leave: LeaveDocument, row: Row): React.ReactElement =>
    row.needsReview ? (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={(event) => {
          event.stopPropagation()
          openLeave(leave)
        }}
        aria-label={`Review leave request from ${row.displayName}`}
      >
        Review
      </Button>
    ) : (
      <TableRowActions>
        <TableRowAction
          icon={Eye}
          label={`View leave request from ${row.displayName}`}
          onSelect={() => openLeave(leave)}
        />
      </TableRowActions>
    )

  const renderTable = (): React.ReactElement => (
    <DataTable
      className="hidden lg:block"
      tableClassName="w-full"
      caption="Leave requests"
    >
      <DataTableHead>
        <tr>
          <DataTableHeadCell className="w-[25%] px-6 py-3">Employee</DataTableHeadCell>
          <DataTableHeadCell className="w-[15%] px-6 py-3">Company</DataTableHeadCell>
          <DataTableHeadCell className="w-[10%] px-6 py-3">Type</DataTableHeadCell>
          <DataTableHeadCell className="w-[20%] px-6 py-3">Dates</DataTableHeadCell>
          <DataTableHeadCell className="w-[11%] px-6 py-3">Submitted</DataTableHeadCell>
          <DataTableHeadCell className="w-[10%] px-6 py-3">Status</DataTableHeadCell>
          <DataTableHeadCell align="right" className="w-[9%] px-6 py-3">
            <span className="sr-only">Actions</span>
          </DataTableHeadCell>
        </tr>
      </DataTableHead>
      <DataTableBody>
        {leaves.map((leave) => {
          const row = describeLeave(leave)
          return (
            <DataTableRow
              key={leave._id}
              className="group/row cursor-pointer"
              onClick={() => handleRowActivate(leave)}
            >
              <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                {renderPerson(row, 'md')}
              </DataTableCell>
              <DataTableCell noWrap={false} className="px-6 py-3 align-middle">
                {renderCompany(row)}
              </DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle text-gray-900 dark:text-gray-100">
                {row.type}
              </DataTableCell>
              <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                {renderDates(row)}
              </DataTableCell>
              <DataTableCell
                className="px-6 py-3 align-middle text-gray-600 dark:text-gray-400"
                title={row.submittedTitle}
              >
                {row.submitted}
              </DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">
                {renderStatus(leave)}
              </DataTableCell>
              <DataTableCell align="right" className="px-6 py-3 align-middle">
                {renderAction(leave, row)}
              </DataTableCell>
            </DataTableRow>
          )
        })}
      </DataTableBody>
    </DataTable>
  )

  /** Phone and tablet layout (the table needs ~1000px once the sidebar is open). */
  const renderCards = (): React.ReactElement => (
    <ul className="flex flex-col gap-2 lg:hidden">
      {leaves.map((leave) => {
        const row = describeLeave(leave)
        return (
          <li
            key={leave._id}
            className="group/row cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors active:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:active:bg-gray-700/40"
            onClick={() => handleRowActivate(leave)}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                {renderPerson(row, 'lg')}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  {renderCompany(row)}
                  <span className="text-gray-500 dark:text-gray-400">{row.type}</span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                {renderStatus(leave, 'sm')}
                {renderAction(leave, row)}
              </div>
            </div>
            <div className="mt-3 flex items-end justify-between gap-3 border-t border-gray-100 pt-3 text-sm dark:border-gray-700/60">
              {renderDates(row)}
              <span
                className="shrink-0 text-xs text-gray-500 dark:text-gray-400"
                title={row.submittedTitle}
              >
                {row.submitted}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )

  return (
    <>
      <PageShell
        breadcrumbItems={[
          { label: 'Time & Attendance', to: '/dashboard' },
          { label: 'Leave' },
        ]}
        icon={TreePalm}
        title="Leave"
        description="Cross-company leave requests across every company you can read."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={<WorkspaceCompanyChips filter={filter} />}
            actions={
              canCreateLeave || canManageTypes ? (
                <div className="flex items-center gap-2">
                  {canManageTypes ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setTypesOpen(true)}
                    >
                      <Settings2 className="h-4 w-4" aria-hidden />
                      <span className="ml-1.5">Leave types</span>
                    </Button>
                  ) : null}
                  {canCreateLeave ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => setCreateOpen(true)}
                      disabled={!createCompanyId}
                      title={createCompanyId ? undefined : 'Select one company first'}
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      <span className="ml-1.5">Request leave</span>
                    </Button>
                  ) : null}
                </div>
              ) : null
            }
          >
            <WorkspaceToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Search name or email…"
              ariaLabel="Search leave requests"
              active={Boolean(debouncedSearch)}
            />
          </WorkspaceToolbar>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex min-w-0 max-w-full items-center gap-2">
              <span className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                Status
              </span>
              <div className="min-w-0 overflow-x-auto [scrollbar-width:none]">
                <SegmentedControl
                  ariaLabel="Filter by status"
                  size="sm"
                  options={[...STATUS_FILTER_OPTIONS]}
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value as StatusFilter)
                    setPage(1)
                  }}
                  className="w-max max-w-none"
                />
              </div>
            </div>

            {hasActiveFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-gray-600 dark:text-gray-400"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                <span className="ml-1">Reset</span>
              </Button>
            ) : null}

            <p
              className="ml-auto text-xs text-gray-500 dark:text-gray-400"
              aria-live="polite"
            >
              {isLoading
                ? 'Loading requests…'
                : `${total} ${total === 1 ? 'request' : 'requests'}`}
            </p>
          </div>

          {error ? (
            <QueryErrorState
              error={error}
              title="Couldn't load leave requests"
              onRetry={refetch}
            />
          ) : isLoading ? (
            <DataTableCard>
              <TableSkeleton rows={limit > 10 ? 10 : limit} cols={7} />
            </DataTableCard>
          ) : leaves.length === 0 ? (
            <EmptyState
              icon={TreePalm}
              title={
                hasActiveFilters
                  ? 'No leave requests match these filters'
                  : 'No leave requests'
              }
              description={
                hasActiveFilters
                  ? 'Try a different company, status or search.'
                  : 'Requests appear here when staff ask for time off.'
              }
              action={
                hasActiveFilters ? (
                  <Button variant="secondary" size="sm" onClick={handleResetFilters}>
                    Reset filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              {renderCards()}
              {renderTable()}

              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onLimitChange={(next) => {
                  setLimit(next)
                  setPage(1)
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                className="px-6 py-3"
              />
            </>
          )}
        </div>
      </PageShell>
      <LeaveTypesModal open={typesOpen} onClose={() => setTypesOpen(false)} />

      <LeaveDetailModal
        companyId={selected?.companyId ?? ''}
        leaveId={selected?._id ?? null}
        summary={selected}
        companyName={
          selected ? filter.companyNameById.get(selected.companyId) : undefined
        }
        onClose={() => setSelected(null)}
        onActionComplete={() => setSelected(null)}
      />

      {createCompanyId && (
        <CreateLeaveModal
          companyId={createCompanyId}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </>
  )
}

WorkspaceLeavesPage.displayName = 'WorkspaceLeavesPage'
